/**
 * Client Booking Service - Public APIs (No JWT Required)
 * 
 * These APIs are for customer-facing booking flows where users don't need to be authenticated.
 * Base path: /v1/online/book/
 * 
 * No JWT tokens required - public access for customers
 */

import axios from 'axios';
import { getApiUrl } from '../../hooks/commanFunc';

// Create axios instance for public booking APIs (no JWT required)
const publicAPI = axios.create({
  baseURL: getApiUrl(), // Uses dev server: https://mapi.dev.miira.ca/v1
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

// Response interceptor for error handling
publicAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Client Booking API Error:', error);
    
    if (error.response) {
      // Server responded with error status
      return Promise.reject({
        message: error.response.data?.msg || 'Request failed',
        code: error.response.data?.code || error.response.status,
        data: error.response.data
      });
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      });
    } else {
      // Something else happened
      return Promise.reject({
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

// ============================================
// PUBLIC BOOKING APIs (No JWT Required)
// ============================================

/**
 * Get available services for booking
 * @param {Object} params - Query parameters
 * @param {string} params.storeID - Store identifier
 * @param {boolean} params.onlineOnly - Only return services available for online booking
 * @param {string} params.providerID - Optional: Filter by provider
 * @returns {Promise<Object>} Service list
 */
export const getPublicServiceList = async (params) => {
  try {
    const { storeID, onlineOnly, providerID } = params;
    let queryString = `storeID=${storeID}`;
    if (onlineOnly) queryString += `&onlineOnly=${onlineOnly}`;
    if (providerID) queryString += `&providerID=${providerID}`;
    
    const response = await publicAPI.get(`/online/book/getServicelist?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Get Public Service List Error:', error);
    throw error;
  }
};

/**
 * Get available service providers
 * @param {string} storeID - Store identifier
 * @returns {Promise<Object>} Provider list
 */
export const getPublicServiceProviders = async (storeID) => {
  try {
    const response = await publicAPI.get(`/online/book/providers?storeID=${storeID}`);
    return response.data;
  } catch (error) {
    console.error('Get Public Service Providers Error:', error);
    throw error;
  }
};

/**
 * Get available time slots for booking
 * @param {Object} params - Query parameters
 * @param {string} params.storeID - Store identifier
 * @param {string} params.bookingType - Type of booking ('service' or 'dining')
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string} params.serviceIDs - Comma-separated service IDs
 * @param {string} params.providerID - Optional: Provider ID for specific provider
 * @returns {Promise<Object>} Available slots
 */
export const getPublicAvailableSlots = async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await publicAPI.get(`/online/book/available-slots?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Get Public Available Slots Error:', error);
    throw error;
  }
};

/**
 * Create a new booking (public - no authentication required)
 * @param {Object} bookingData - Booking information
 * @param {string} bookingData.storeID - Store identifier
 * @param {string} bookingData.bookingType - 'dining' or 'service'
 * @param {string} bookingData.bookingDate - Date in YYYY-MM-DD format
 * @param {string} bookingData.startTime - Time in HH:mm format
 * @param {string} bookingData.customerName - Customer's full name
 * @param {string} bookingData.customerPhone - Customer's phone number
 * @param {string} bookingData.customerEmail - Customer's email (optional)
 * @param {string} bookingData.notes - Special requests (optional)
 * @param {string} bookingData.source - 'online' (fixed for public bookings)
 * @param {Array<string>} bookingData.serviceIDs - Array of service IDs
 * @param {string} bookingData.providerID - Provider ID (null for auto-assignment)
 * @returns {Promise<Object>} Created booking details
 */
export const createPublicBooking = async (bookingData) => {
  try {
    const response = await publicAPI.post('/online/book/create', bookingData);
    return response.data;
  } catch (error) {
    console.error('Create Public Booking Error:', error);
    throw error;
  }
};

/**
 * Get booking details by booking code (for customer reference)
 * @param {string} bookingCode - Unique booking code (e.g., BK-20260830-001)
 * @param {string} customerPhone - Customer's phone for verification
 * @returns {Promise<Object>} Booking details
 */
export const getPublicBookingDetails = async (bookingCode, customerPhone) => {
  try {
    const response = await publicAPI.get(`/online/book/details?bookingCode=${bookingCode}&phone=${customerPhone}`);
    return response.data;
  } catch (error) {
    console.error('Get Public Booking Details Error:', error);
    throw error;
  }
};

/**
 * Cancel a booking (public - customer can cancel their own booking)
 * @param {string} bookingCode - Unique booking code
 * @param {string} customerPhone - Customer's phone for verification
 * @param {string} reason - Cancellation reason (optional)
 * @returns {Promise<Object>} Cancellation confirmation
 */
export const cancelPublicBooking = async (bookingCode, customerPhone, reason = '') => {
  try {
    const response = await publicAPI.post('/online/book/cancel', {
      bookingCode,
      customerPhone,
      reason
    });
    return response.data;
  } catch (error) {
    console.error('Cancel Public Booking Error:', error);
    throw error;
  }
};

/**
 * Get booking settings/configuration for a store (public info)
 * @param {string} storeID - Store identifier
 * @returns {Promise<Object>} Public booking settings
 */
export const getPublicBookingSettings = async (storeID) => {
  try {
    const response = await publicAPI.get(`/online/book/settings?storeID=${storeID}`);
    return response.data;
  } catch (error) {
    console.error('Get Public Booking Settings Error:', error);
    throw error;
  }
};

/**
 * Check if a specific time slot is still available (real-time check before booking)
 * @param {Object} params - Verification parameters
 * @param {string} params.storeID - Store identifier
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string} params.time - Time in HH:mm format
 * @param {string} params.serviceIDs - Comma-separated service IDs
 * @param {string} params.providerID - Provider ID (if specific)
 * @returns {Promise<Object>} Slot availability status
 */
export const verifySlotAvailability = async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await publicAPI.get(`/online/book/verify-slot?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Verify Slot Availability Error:', error);
    throw error;
  }
};

// Export all functions as default object
const clientBookingService = {
  getPublicServiceList,
  getPublicServiceProviders,
  getPublicAvailableSlots,
  createPublicBooking,
  getPublicBookingDetails,
  cancelPublicBooking,
  getPublicBookingSettings,
  verifySlotAvailability
};

export default clientBookingService;
